/**
 * Статья базы знаний: текст действующей версии с пометками «заменено», проверка и публикация, версии и их
 * сравнение, связи с записями портала, настройки карточки (БЗ-07–09, БЗ-11).
 */
import {
  KB_LINK_TYPES,
  type KbArticleView,
  type KbLinkType,
  type KbReplacedPart,
  type KbVersionFull,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Archive, Check, Pencil, Send, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { type CardDraft, CardFields, type VersionDraft, VersionFields } from './ArticleForm';
import {
  ArticleBadges,
  ArticleText,
  DiffView,
  diffLines,
  metaQuery,
  sectionTitle,
  VERSION_TITLE,
  VERSION_TONE,
} from './shared';

const draftOf = (
  v: Pick<KbVersionFull, 'title' | 'summary' | 'body' | 'changeNote' | 'replacedParts'> | null,
): VersionDraft => ({
  title: v?.title ?? '',
  summary: v?.summary ?? '',
  body: v?.body ?? '',
  changeNote: v?.changeNote ?? '',
  replacedParts: v?.replacedParts ?? [],
});

const payload = (d: VersionDraft) => ({
  title: d.title,
  summary: d.summary || null,
  body: d.body,
  changeNote: d.changeNote || null,
  replacedParts: d.replacedParts.filter((p) => p.label && p.replacedById) as KbReplacedPart[],
});

function Compare({ id, versions }: { id: string; versions: KbArticleView['versions'] }) {
  const [a, setA] = useState(versions[1]?.id ?? '');
  const [b, setB] = useState(versions[0]?.id ?? '');
  const [shown, setShown] = useState(false);
  const bodies = useQuery({
    queryKey: ['kb', 'compare', id, a, b, shown],
    queryFn: async () => {
      const [x, y] = await Promise.all([
        api<KbVersionFull>(`/kb/versions/${a}`),
        api<KbVersionFull>(`/kb/versions/${b}`),
      ]);
      return { x, y };
    },
    enabled: shown && Boolean(a && b),
  });
  const rows = useMemo(
    () => (bodies.data ? diffLines(bodies.data.x.body, bodies.data.y.body) : []),
    [bodies.data],
  );
  if (versions.length < 2)
    return <p className="text-[14px] text-ink-2">Сравнивать пока нечего: версия одна.</p>;
  const opt = versions.map((v) => (
    <option key={v.id} value={v.id}>
      Версия {v.version} — {VERSION_TITLE[v.status]}
    </option>
  ));
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <Field label="Было">
          <Select
            value={a}
            onChange={(e) => {
              setA(e.target.value);
              setShown(false);
            }}
          >
            {opt}
          </Select>
        </Field>
        <Field label="Стало">
          <Select
            value={b}
            onChange={(e) => {
              setB(e.target.value);
              setShown(false);
            }}
          >
            {opt}
          </Select>
        </Field>
        <Button onClick={() => setShown(true)} disabled={a === b}>
          Сравнить
        </Button>
      </div>
      {shown && bodies.isPending ? <Spinner /> : null}
      {bodies.error ? <ErrorBox error={bodies.error} /> : null}
      {bodies.data ? <DiffView rows={rows} /> : null}
    </div>
  );
}

export function ArticlePage() {
  const { articleId } = useParams({ strict: false }) as { articleId: string };
  const qc = useQueryClient();
  const meta = useQuery(metaQuery);
  const q = useQuery({
    queryKey: ['kb', 'article', articleId],
    queryFn: () => api<KbArticleView>(`/kb/articles/${articleId}`),
  });
  const a = q.data;
  useCrumbs(a ? [{ label: a.number, title: a.title }] : null, q.error);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<VersionDraft>(draftOf(null));
  const [showReject, setShowReject] = useState(false);
  const [rejectText, setRejectText] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [card, setCard] = useState<CardDraft | null>(null);
  const [link, setLink] = useState<{ type: KbLinkType; key: string; label: string }>({
    type: 'section',
    key: '',
    label: '',
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['kb'] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
  };
  const fail = (e: unknown) => toast(e instanceof Error ? e.message : 'Не получилось', 'danger');
  const call = (path: string, body?: unknown, method: 'POST' | 'PATCH' | 'DELETE' = 'POST') =>
    api(path, { method, body: body ?? {} });
  const useMut = (fn: () => Promise<unknown>, ok: string, after?: () => void) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        refresh();
        toast(ok);
        after?.();
      },
      onError: fail,
    });

  const working = a?.working ?? null;
  useEffect(() => {
    if (a) setDraft(draftOf(working ?? a.published));
  }, [a, working]);

  const saveDraft = useMut(
    async () => {
      if (working && (working.status === 'draft' || working.status === 'rejected'))
        await call(`/kb/versions/${working.id}`, payload(draft), 'PATCH');
      else await call(`/kb/articles/${articleId}/versions`, payload(draft));
    },
    'Черновик сохранён',
    () => setEditing(false),
  );
  const submit = useMut(async () => {
    if (!working) return;
    await call(`/kb/versions/${working.id}/submit`);
  }, 'Отправлено владельцу на проверку');
  const withdraw = useMut(async () => {
    if (working) await call(`/kb/versions/${working.id}/withdraw`);
  }, 'Версия снята с проверки');
  const approve = useMut(async () => {
    if (working) await call(`/kb/versions/${working.id}/approve`);
  }, 'Версия опубликована');
  const reject = useMut(
    async () => {
      if (working) await call(`/kb/versions/${working.id}/reject`, { comment: rejectText });
    },
    'Версия возвращена автору',
    () => {
      setShowReject(false);
      setRejectText('');
    },
  );
  const archive = useMut(async () => {
    await call(`/kb/articles/${articleId}/archive`, { archived: a?.status !== 'archived' });
  }, 'Готово');
  const saveCard = useMut(
    async () => {
      if (!card) return;
      await call(
        `/kb/articles/${articleId}`,
        {
          section: card.section,
          ownerId: card.ownerId || null,
          ownerUnit: card.ownerUnit || null,
          visibleRoles: card.visibleRoles,
          reviewEveryMonths: Number(card.reviewEveryMonths) || 12,
        },
        'PATCH',
      );
    },
    'Карточка сохранена',
    () => setShowCard(false),
  );
  const addLink = useMut(
    async () => {
      const isId = /^[0-9a-f-]{36}$/i.test(link.key.trim());
      await call(`/kb/articles/${articleId}/links`, {
        targetType: link.type,
        ...(isId ? { targetId: link.key.trim() } : { targetKey: link.key.trim() }),
        label: link.label || null,
      });
    },
    'Связь добавлена',
    () => setLink({ ...link, key: '', label: '' }),
  );

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.error || !a)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const shown = working && editing ? null : a.published;
  const replacerLabel = (id: string) => a.replacers.find((r) => r.id === id)?.number ?? 'другая статья';

  return (
    <Page>
      <PageHeader
        title={<span className="[overflow-wrap:anywhere]">{a.title}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="num">{a.number}</span>
            <span>{sectionTitle(meta.data, a.section)}</span>
            {a.ownerName ? (
              <span>· владелец {a.ownerName}</span>
            ) : a.ownerUnit ? (
              <span>· {a.ownerUnit}</span>
            ) : (
              <span>· владельца нет</span>
            )}
            <ArticleBadges a={a} />
          </span>
        }
        actions={
          <>
            {a.canEdit && !editing ? (
              <Button
                icon={<Pencil className="size-4" />}
                onClick={() => {
                  setDraft(draftOf(working ?? a.published));
                  setEditing(true);
                }}
              >
                {working ? 'Править версию' : 'Новая версия'}
              </Button>
            ) : null}
            {a.canEdit ? (
              <Button
                onClick={() => {
                  setCard({
                    section: a.section,
                    ownerId: a.ownerId ?? '',
                    ownerUnit: a.ownerUnit ?? '',
                    visibleRoles: a.visibleRoles,
                    reviewEveryMonths: String(a.reviewEveryMonths),
                  });
                  setShowCard(true);
                }}
              >
                Настройки
              </Button>
            ) : null}
          </>
        }
      />
      {a.replacedById ? (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-[14px] text-ink">
          Статья заменена{a.replacedSince ? ` с ${date(a.replacedSince)}` : ''}
          {a.replacedByNumber ? (
            <>
              {' '}
              статьёй{' '}
              <Link
                to="/kb/$articleId"
                params={{ articleId: a.replacedById }}
                className="font-medium underline"
              >
                {a.replacedByNumber} {a.replacedByTitle}
              </Link>
            </>
          ) : null}
          . Pilot по ней не отвечает.
        </div>
      ) : null}
      {a.reviewOverdue ? (
        <div className="mb-4 rounded-md border border-danger/30 bg-danger-soft px-4 py-3 text-[14px] text-ink">
          Срок пересмотра вышел {date(a.nextReviewAt)}.{' '}
          {a.isOwner ? 'Подтвердите текст новой версией.' : 'Владелец статьи получил дело.'}
        </div>
      ) : null}

      {working && !editing ? (
        <Panel
          className="mb-4"
          title={`Версия ${working.version}: ${VERSION_TITLE[working.status]}`}
          actions={
            <Badge tone={VERSION_TONE[working.status] ?? 'neutral'}>{VERSION_TITLE[working.status]}</Badge>
          }
        >
          {working.reviewComment ? (
            <p className="mb-3 text-[14px] text-ink">Комментарий проверяющего: {working.reviewComment}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {(working.status === 'draft' || working.status === 'rejected') && a.canEdit ? (
              <Button
                variant="primary"
                icon={<Send className="size-4" />}
                loading={submit.isPending}
                onClick={() => submit.mutate()}
              >
                Отправить на проверку
              </Button>
            ) : null}
            {working.status === 'review' && a.canEdit ? (
              <Button icon={<Undo2 className="size-4" />} onClick={() => withdraw.mutate()}>
                Снять с проверки
              </Button>
            ) : null}
            {working.status === 'review' && a.canReview ? (
              <>
                <Button
                  variant="primary"
                  icon={<Check className="size-4" />}
                  loading={approve.isPending}
                  onClick={() => approve.mutate()}
                >
                  Опубликовать
                </Button>
                <Button variant="danger" icon={<X className="size-4" />} onClick={() => setShowReject(true)}>
                  Вернуть автору
                </Button>
              </>
            ) : null}
            {working.status === 'review' && !a.canReview ? (
              <p className="text-[14px] text-ink-2">
                Версию проверяет владелец статьи{a.ownerName ? ` — ${a.ownerName}` : ''}.
              </p>
            ) : null}
          </div>
          <details className="mt-4">
            <summary className="flex min-h-11 cursor-pointer items-center text-[14px] font-medium">
              Текст этой версии
            </summary>
            <ArticleText body={working.body} replaced={working.replacedParts} replacerLabel={replacerLabel} />
          </details>
        </Panel>
      ) : null}

      {editing ? (
        <Panel title={working ? `Правка версии ${working.version}` : 'Новая версия'} className="mb-4">
          <VersionFields value={draft} onChange={setDraft} articleId={articleId} showNote />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="primary"
              loading={saveDraft.isPending}
              disabled={draft.title.trim().length < 3}
              onClick={() => saveDraft.mutate()}
            >
              Сохранить черновик
            </Button>
            <Button onClick={() => setEditing(false)}>Отмена</Button>
          </div>
        </Panel>
      ) : null}

      {shown ? (
        <Panel
          title={`Действует: версия ${shown.version}`}
          meta={shown.publishedAt ? `опубликована ${date(shown.publishedAt)}` : undefined}
          className="mb-4"
        >
          <ArticleText body={shown.body} replaced={shown.replacedParts} replacerLabel={replacerLabel} />
        </Panel>
      ) : !working && !editing ? (
        <Panel className="mb-4">
          <p className="text-[14px] text-ink-2">Опубликованной версии пока нет.</p>
        </Panel>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Связанные записи" meta="где эта статья показывается в портале">
          {a.links.length ? (
            <ul className="mb-3">
              {a.links.map((l) => (
                <li
                  key={l.id}
                  className="flex min-h-11 items-center justify-between gap-2 border-b border-line/60 py-1.5 text-[14px]"
                >
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    <span className="text-ink-2">{KB_LINK_TYPES[l.targetType]}: </span>
                    {l.path ? (
                      <Link to={l.path} className="font-medium underline">
                        {l.label ?? l.targetKey ?? 'открыть'}
                      </Link>
                    ) : (
                      <span className="font-medium">{l.label ?? l.targetKey ?? l.targetId}</span>
                    )}
                  </span>
                  {a.canEdit ? (
                    <IconButton
                      label="Убрать связь"
                      onClick={() =>
                        call(`/kb/articles/${articleId}/links/${l.id}`, undefined, 'DELETE')
                          .then(refresh)
                          .catch(fail)
                      }
                    >
                      <X className="size-4" />
                    </IconButton>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-3 text-[14px] text-ink-2">Статья пока ни с чем не связана.</p>
          )}
          {a.canEdit ? (
            <div className="grid gap-2 md:grid-cols-[10rem_1fr_auto] md:items-end">
              <Field label="С чем">
                <Select
                  value={link.type}
                  onChange={(e) => setLink({ ...link, type: e.target.value as KbLinkType })}
                >
                  {(Object.keys(KB_LINK_TYPES) as KbLinkType[]).map((t) => (
                    <option key={t} value={t}>
                      {KB_LINK_TYPES[t]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Код или идентификатор" hint="Для раздела портала — его код (например schedule)">
                <Input value={link.key} onChange={(e) => setLink({ ...link, key: e.target.value })} />
              </Field>
              <Button
                disabled={!link.key.trim()}
                loading={addLink.isPending}
                onClick={() => addLink.mutate()}
              >
                Связать
              </Button>
            </div>
          ) : null}
        </Panel>

        <Panel title="Версии" meta={`${a.versions.length}`}>
          <ul className="mb-4">
            {a.versions.map((v) => (
              <li key={v.id} className="flex flex-col gap-0.5 border-b border-line/60 py-2 text-[14px]">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">Версия {v.version}</span>
                  <Badge tone={VERSION_TONE[v.status] ?? 'neutral'}>{VERSION_TITLE[v.status]}</Badge>
                </span>
                <span className="text-[13px] text-ink-2">
                  {v.authorName ?? '—'} · {dateTime(v.publishedAt ?? v.submittedAt ?? v.createdAt)}
                  {v.reviewedByName ? ` · проверил ${v.reviewedByName}` : ''}
                </span>
                {v.changeNote ? <span className="text-[13px] text-ink-2">{v.changeNote}</span> : null}
              </li>
            ))}
          </ul>
          <Compare id={articleId} versions={a.versions} />
        </Panel>
      </div>

      {a.canEdit && (a.isOwner || meta.data?.isAdmin) ? (
        <div className="mt-6">
          <Button variant="danger" icon={<Archive className="size-4" />} onClick={() => archive.mutate()}>
            {a.status === 'archived' ? 'Вернуть из архива' : 'Убрать в архив'}
          </Button>
        </div>
      ) : null}
      <Dialog
        open={showReject}
        onOpenChange={setShowReject}
        title="Вернуть автору"
        footer={
          <>
            <Button onClick={() => setShowReject(false)}>Отмена</Button>
            <Button
              variant="danger"
              disabled={rejectText.trim().length < 3}
              loading={reject.isPending}
              onClick={() => reject.mutate()}
            >
              Вернуть
            </Button>
          </>
        }
      >
        <Field label="Что исправить" htmlFor="kb-reject">
          <Textarea
            id="kb-reject"
            value={rejectText}
            onChange={(e) => setRejectText(e.target.value)}
            rows={4}
          />
        </Field>
      </Dialog>
      <Dialog
        open={showCard}
        onOpenChange={setShowCard}
        title="Настройки статьи"
        width="lg"
        footer={
          <>
            <Button onClick={() => setShowCard(false)}>Отмена</Button>
            <Button variant="primary" loading={saveCard.isPending} onClick={() => saveCard.mutate()}>
              Сохранить
            </Button>
          </>
        }
      >
        {card ? <CardFields value={card} onChange={setCard} /> : null}
      </Dialog>
    </Page>
  );
}
